/**
 * PRD.305 — the ONE ordering drawer, shared by the Laboratory module, Patient
 * Workspace Labs and Consultation Labs.
 *
 * Wherever it is opened from, it writes the same canonical order and the order
 * lands in the same Laboratory queue — the PRD's requirement that clinical
 * ordering and laboratory operations share one model, not three.
 *
 * The catalog is `appointment_services` rows flagged `is_lab_test`. Free-text
 * tests are allowed for one-offs; they simply carry no `service_id` and so are
 * not billable against a catalog price.
 *
 * Billing is OPTIONAL and explicit. PRD §4 lets an order exist before payment
 * where the clinic's own rules allow it, so this drawer never forces an
 * invoice — it offers one, reusing the existing billing subsystem. Payment
 * never gates order creation or entry into the Laboratory queue.
 *
 * When opened WITHOUT a patient context (the Laboratory module's
 * "+ New Lab Order"), it becomes a step wizard modelled directly on PRD.290's
 * New Pharmacy Sale, so the two operational modules read the same way:
 *
 *   1. Search  — existing-patient search + today's LAB REQUESTS across all
 *                patients. A "lab request" is the laboratory analog of a
 *                prescription: an order a clinician already raised that still
 *                has tests nobody has billed and settled.
 *   2. Choose  — once a patient is picked, the list is replaced by THAT
 *                patient's requests, sectioned Today / Earlier / Already
 *                billed. Exactly one eligible → auto-selected. None → continue
 *                straight to a new order.
 *   3. Order   — either bill the picked request's unbilled tests, or build a
 *                brand-new order (catalog + one-off tests, notes, billing).
 *
 * No writes happen on patient or request selection. A consultation may
 * optionally be attached to a NEW order — the open encounter is offered first —
 * but a walk-in order needs no visit at all.
 *
 * PRD.329 — in the Laboratory module a NEW order is a digital REQUISITION:
 *
 *   3. Requisition — requisition date, priority, referring doctor, brief
 *                    clinical details, and tests picked from the catalog
 *                    grouped by Laboratory Department (several departments
 *                    → still ONE order).
 *   4. Review      — the selected tests at catalog prices, totalled with the
 *                    shared invoice summary (no parallel laboratory total).
 *   5. Created     — `createLabRequisition` has written the order, its tests,
 *                    a DEDICATED invoice and its lines in ONE local write
 *                    transaction. Pay Now opens the existing Billing panel
 *                    (`BillingInvoicesPanel`); Finish / Pay Later just closes.
 *                    Payment never gates the order, which is already live in
 *                    Laboratory → Flow at `ordered`.
 *
 * The in-chart surfaces (Patient Workspace, Consultation) keep the PRD.305
 * single-step order — including the consultation's `requested` semantics — and
 * share the department-grouped picker and department snapshots.
 */
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qLabTestCatalog,
  qPatientsDrawerList,
  qEncountersTodayByPatient,
  qTodaysLabRequests,
  qPatientLabRequests,
  qLabOrderTests,
  qNoOp,
  qPatientById,
  qAppointmentProviders,
  resolvePatientAgeDisplay,
  LAB_PRIORITIES,
  LAB_PRIORITY_LABELS,
  unpricedRequisitionDrafts,
  buildLabOrderTestDrafts,
  labOrderDraftTotalCents,
  resolveLabOrderAnchor,
  isLabRequestEligible,
  labRequestStatusHint,
  sectionLabRequests,
  unbilledLabTestIds,
  type LabTestCatalogRow,
  type PatientDrawerRow,
  type LabOrderEncounterCandidate,
  type LabRequestRow,
  type LabOrderTestRow,
  type TreatmentRecordDrawerWidth,
  type PatientDetailRow,
  type AppointmentProviderRow,
  type LabPriority,
} from '@helios/data-access';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import { NavIcon, useBrandTokens } from '../../shell/shared';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { createLabOrder, billLabTests } from '../../services/laboratory-service';
import {
  createLabRequisition,
  type LabRequisitionResult,
} from '../../services/lab-requisition-service';
import { invoiceSummary } from '../patient/consultation/consultation-data';
import { BillingInvoicesPanel } from '../patient/encounters/TreatmentInvoiceDrawer';
import { DRAWER_WIDTH_BY_PRESET } from '../patient/encounters/TreatmentChartingFilesDrawer';
import { LabEmptyState, LabStatusBadge, formatLabDate } from './lab-shared';
import { LabRequisitionTestPicker } from './LabRequisitionTestPicker';

export interface LabOrderContext {
  patientId: string;
  patientName: string;
  clinicId?: string | null;
  visitId?: string | null;
  encounterId?: string | null;
  appointmentId?: string | null;
  queueId?: string | null;
  orderingProviderId?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-resolved patient context. `null` + `allowPatientSelection` = walk-in. */
  context: LabOrderContext | null;
  /** Which surface raised the order — recorded on `lab_orders.source`. */
  source: 'consultation' | 'patient_workspace' | 'laboratory';
  /** Whether the current user may add the tests to an invoice. */
  canBill?: boolean;
  /**
   * Show the patient/request wizard when no `context` is supplied. Used by the
   * Laboratory module, which has no patient in scope until one is chosen.
   */
  allowPatientSelection?: boolean;
  /**
   * Billing drawer width preset, so the wizard matches New Pharmacy Sale's
   * footprint. Ignored in the narrow in-chart mode.
   */
  drawerWidth?: TreatmentRecordDrawerWidth;
  onCreated?: (orderId: string) => void;
}

interface SelectedPatient {
  id: string;
  name: string;
  number: string | null;
}

// ─── Shared bits (mirroring New Pharmacy Sale's row/list vocabulary) ───

const fieldLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-medium)',
  color: 'var(--text)',
};

const textInputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: 'var(--font-size-sm)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border)',
  outline: 'none',
  boxSizing: 'border-box',
};

const searchIconStyle: CSSProperties = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  pointerEvents: 'none',
  color: 'var(--text-muted)',
};

const listBoxStyle: CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  overflow: 'hidden',
};

const resultsBoxStyle: CSSProperties = {
  ...listBoxStyle,
  maxHeight: 240,
  overflowY: 'auto',
};

const mutedRowStyle: CSSProperties = {
  padding: 'var(--sp-3)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text-muted)',
};

const dashedEmptyStyle: CSSProperties = {
  padding: 'var(--sp-5)',
  border: '1px dashed var(--border)',
  borderRadius: 'var(--radius-md)',
  textAlign: 'center',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text-muted)',
};

const rowTitleStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-medium)',
  color: 'var(--text)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const rowSubStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-muted)',
};

const hintStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-muted)',
  lineHeight: 1.5,
};

const resultRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--sp-2)',
  width: '100%',
  textAlign: 'left',
  padding: 'var(--sp-3)',
  background: 'transparent',
  border: 'none',
  borderBottom: '1px solid var(--border)',
  cursor: 'pointer',
};

/** The selected-patient bar, with a Change affordance back to the search step. */
function PatientSummaryBar({
  patient,
  onChange,
}: {
  patient: SelectedPatient;
  onChange: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-3)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface-2)',
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={rowTitleStyle}>{patient.name}</span>
        {patient.number && <span style={rowSubStyle}>P-{patient.number}</span>}
      </span>
      <Button variant="outline" size="sm" onClick={onChange}>
        Change
      </Button>
    </div>
  );
}

/**
 * One lab-request row. Mirrors Pharmacy's prescription row: identity on the
 * left, a billing-progress hint on the right, and a single Use action.
 */
function LabRequestListRow({
  row,
  showPatient = false,
  disabled = false,
  onUse,
}: {
  row: LabRequestRow;
  showPatient?: boolean;
  disabled?: boolean;
  onUse: (row: LabRequestRow) => void;
}) {
  const tests = Number(row.billable_tests ?? 0);
  const meta = [
    row.order_number ?? 'Lab request',
    formatLabDate(row.ordered_at),
    row.provider_name,
    `${tests} test${tests === 1 ? '' : 's'}`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-3)',
        borderBottom: '1px solid var(--border)',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={rowTitleStyle}>
          {showPatient ? (row.patient_name ?? 'Unknown') : (row.order_number ?? 'Lab request')}
        </span>
        <span style={rowSubStyle}>
          {showPatient ? meta : meta.split(' · ').slice(1).join(' · ')}
        </span>
      </span>
      <span style={{ ...rowSubStyle, whiteSpace: 'nowrap' }}>{labRequestStatusHint(row)}</span>
      <Button variant="outline" size="sm" disabled={disabled} onClick={() => onUse(row)}>
        Use
      </Button>
    </div>
  );
}

/** `YYYY-MM-DDTHH:mm` in local time, for a datetime-local input. */
function localDateTimeValue(d = new Date()): string {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

/** The requisition's local datetime → ISO instant (falls back to now). */
function requisitionInstant(local: string): string {
  const parsed = local ? new Date(local) : new Date();
  return Number.isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
}

type WizardStep = 'search' | 'choose' | 'order' | 'requisition' | 'review' | 'done' | 'pay';

/** Referring doctor select value for "someone outside the clinic". */
const EXTERNAL_REFERRER = '__external__';

export function CreateLabOrderDrawer({
  open,
  onOpenChange,
  context,
  source,
  canBill = false,
  allowPatientSelection = false,
  drawerWidth = 'standard',
  onCreated,
}: Props) {
  const brandTokens = useBrandTokens();
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';
  const formatMoney = useFormatMoney();

  const { data: catalog = [] } = usePowerSyncQuery<LabTestCatalogRow>(qLabTestCatalog());

  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** PRD.329 — per-test specimen / details (→ lab_order_tests.notes). */
  const [testNotes, setTestNotes] = useState<Record<string, string>>({});
  const [freeText, setFreeText] = useState('');
  const [freeTextTests, setFreeTextTests] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [addToInvoice, setAddToInvoice] = useState(true);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Wizard state (Laboratory module walk-in) ──
  const [step, setStep] = useState<WizardStep>('search');
  // ── PRD.329 requisition state ──
  const [requisitionDate, setRequisitionDate] = useState(() => localDateTimeValue());
  const [priority, setPriority] = useState<LabPriority>('routine');
  /** '' = none, a provider id, or EXTERNAL_REFERRER. */
  const [referrer, setReferrer] = useState('');
  const [externalReferrer, setExternalReferrer] = useState('');
  /**
   * Layer 1 identity: minted when the drawer opens and kept until the
   * requisition commits, so Retry after a failure re-derives the SAME ids and
   * converges instead of creating a second order.
   */
  const [requisitionId, setRequisitionId] = useState(() => crypto.randomUUID());
  const [created, setCreated] = useState<LabRequisitionResult | null>(null);
  /** D5 — an in-chart order whose billing step failed (shown, never swallowed). */
  const [billingFailure, setBillingFailure] = useState<string | null>(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [pickedPatient, setPickedPatient] = useState<SelectedPatient | null>(null);
  /** A clinician-raised request being billed, instead of creating a new order. */
  const [pickedRequest, setPickedRequest] = useState<LabRequestRow | null>(null);
  /**
   * Chosen consultation. `undefined` = follow the default (open encounter, if
   * any); `null` = the user explicitly chose "No consultation".
   */
  const [encounterChoice, setEncounterChoice] = useState<string | null | undefined>(undefined);

  const wizard = allowPatientSelection && !context;
  const activePatientId = context?.patientId ?? pickedPatient?.id ?? '';
  const todayIso = useMemo(() => {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  }, []);

  const { data: patientRows = [] } = usePowerSyncQuery<PatientDrawerRow>(
    wizard && open
      ? qPatientsDrawerList({ userId, search: patientSearch, tab: 'all' })
      : qNoOp('lab-order-patients'),
  );

  // Section A — today's unsettled requests across all patients.
  const { data: todaysRequests = [] } = usePowerSyncQuery<LabRequestRow>(
    wizard && open ? qTodaysLabRequests(todayIso) : qNoOp('lab-requests-today'),
  );

  // Section B — the chosen patient's full request history (any date).
  const { data: patientRequests = [] } = usePowerSyncQuery<LabRequestRow>(
    wizard && pickedPatient ? qPatientLabRequests(pickedPatient.id) : qNoOp('lab-requests-patient'),
  );

  // Tests of the picked request — the "Add to Invoice" target.
  const { data: requestTests = [] } = usePowerSyncQuery<LabOrderTestRow>(
    pickedRequest ? qLabOrderTests(pickedRequest.lab_order_id) : qNoOp('lab-request-tests'),
  );

  const sections = useMemo(
    () => sectionLabRequests(patientRequests, todayIso),
    [patientRequests, todayIso],
  );

  const billableTestIds = useMemo(() => unbilledLabTestIds(requestTests), [requestTests]);

  // Optional consultation attach — open encounters sort first (PRD.305 §2).
  const { data: encounterRows = [] } = usePowerSyncQuery<
    LabOrderEncounterCandidate & { provider_name?: string | null }
  >(
    wizard && activePatientId && !pickedRequest
      ? qEncountersTodayByPatient(activePatientId)
      : qNoOp('lab-order-encounters'),
  );

  const anchor = useMemo(
    () => resolveLabOrderAnchor(encounterRows, encounterChoice),
    [encounterRows, encounterChoice],
  );

  // ── Transitions (no writes) ──
  const choosePatient = useCallback(
    (p: PatientDrawerRow) => {
      const eligible = todaysRequests.filter((r) => r.patient_id === p.id);
      setPickedPatient({ id: p.id, name: p.full_name ?? 'Unknown', number: p.patient_number });
      setEncounterChoice(undefined);
      if (eligible.length === 1) {
        // Exactly one waiting request → skip the extra step, as Pharmacy does.
        setPickedRequest(eligible[0]);
        setStep('order');
      } else {
        setPickedRequest(null);
        setStep('choose');
      }
    },
    [todaysRequests],
  );

  const useRequest = useCallback((r: LabRequestRow) => {
    if (!r.patient_id) return;
    setPickedPatient(
      (prev) =>
        prev ?? {
          id: r.patient_id as string,
          name: r.patient_name ?? 'Unknown',
          number: r.patient_number,
        },
    );
    setPickedRequest(r);
    setStep('order');
  }, []);

  const startNewOrder = useCallback(() => {
    setPickedRequest(null);
    // PRD.329 — a new order raised in the Laboratory module is a requisition.
    setStep('requisition');
  }, []);

  const changePatient = useCallback(() => {
    setPickedPatient(null);
    setPickedRequest(null);
    setEncounterChoice(undefined);
    setStep('search');
  }, []);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setTestNotes({});
    setFreeText('');
    setFreeTextTests([]);
    setNotes('');
    setSearch('');
    setError(null);
    setRequisitionDate(localDateTimeValue());
    setPriority('routine');
    setReferrer('');
    setExternalReferrer('');
    setRequisitionId(crypto.randomUUID());
    setCreated(null);
    setBillingFailure(null);
    // PRD.305 — a consultation request may be taken to an outside laboratory,
    // so billing is available but NOT pre-selected there. The clinic bills when
    // it accepts the work.
    setAddToInvoice(canBill && source !== 'consultation');
    setPatientSearch('');
    setSearchFocused(false);
    setPickedPatient(null);
    setPickedRequest(null);
    setEncounterChoice(undefined);
    setStep(allowPatientSelection && !context ? 'search' : 'order');
  }, [open, context, allowPatientSelection, canBill, source]);

  /**
   * The exact rows that will be written. Built by the shared, unit-tested
   * builder rather than inline, so the drawer's preview and the persisted order
   * can never disagree — and so non-selectable services are dropped by the same
   * rule everywhere.
   */
  const drafts = useMemo(
    () =>
      buildLabOrderTestDrafts({
        catalog,
        selectedIds: [...selected],
        freeTextNames: freeTextTests,
        notesByServiceId: testNotes,
      }),
    [catalog, selected, freeTextTests, testNotes],
  );

  const totalCents = useMemo(() => labOrderDraftTotalCents(drafts), [drafts]);
  const testCount = drafts.length;

  // PRD.329 — Review step. Totals use the SAME invoice summary the invoice
  // itself is totalled with, over the lines the requisition will write.
  const reviewSummary = useMemo(
    () =>
      invoiceSummary(
        drafts.map((d, i) => ({
          id: String(i),
          serviceName: d.name,
          qty: 1,
          unitPrice: (d.priceCents ?? 0) / 100,
          discount: 0,
          discountType: 'fixed' as const,
          taxRate: 0,
        })),
      ),
    [drafts],
  );
  const unpriced = useMemo(() => unpricedRequisitionDrafts(drafts), [drafts]);

  // Canonical patient identity for the requisition header — displayed, never retyped.
  const { data: patientDetailRows = [] } = usePowerSyncQuery<PatientDetailRow>(
    wizard && pickedPatient ? qPatientById(pickedPatient.id) : qNoOp('lab-req-patient'),
  );
  const patientDetail = patientDetailRows[0] ?? null;
  const patientAge = useMemo(() => resolvePatientAgeDisplay(patientDetail), [patientDetail]);

  const { data: providerRows = [] } = usePowerSyncQuery<AppointmentProviderRow>(
    wizard && open ? qAppointmentProviders() : qNoOp('lab-req-providers'),
  );
  const activeProviders = useMemo(() => providerRows.filter((p) => p.active !== 0), [providerRows]);

  const setTestNote = useCallback((serviceId: string, value: string) => {
    setTestNotes((prev) => ({ ...prev, [serviceId]: value }));
  }, []);

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const addFreeText = useCallback(() => {
    const name = freeText.trim();
    if (!name) return;
    setFreeTextTests((prev) => (prev.includes(name) ? prev : [...prev, name]));
    setFreeText('');
  }, [freeText]);

  /**
   * The patient + anchor this order will be written against. A supplied
   * `context` (Patient Workspace / Consultation) wins; otherwise it comes from
   * the walk-in picker, where BOTH anchor ids may legitimately be null.
   */
  const effectiveContext = useMemo<LabOrderContext | null>(() => {
    if (context) return context;
    if (!pickedPatient) return null;
    return {
      patientId: pickedPatient.id,
      patientName: pickedPatient.name,
      encounterId: anchor.encounterId,
      visitId: anchor.visitId,
    };
  }, [context, pickedPatient, anchor]);

  /**
   * Bill an existing clinician-raised request. This creates NO order — it only
   * adds the still-unbilled tests to the patient's invoice through the same
   * `billLabTests` path the create flow uses, so a partially billed request can
   * be re-opened without double-charging.
   */
  const handleBillRequest = useCallback(async () => {
    if (!db || !pickedRequest || busy || billableTestIds.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await billLabTests({
        db,
        orgId,
        patientId: pickedRequest.patient_id ?? '',
        patientName: pickedRequest.patient_name ?? 'Patient',
        testIds: billableTestIds,
        encounterId: pickedRequest.encounter_id ?? null,
        visitId: pickedRequest.visit_id ?? null,
        date: new Date().toISOString().slice(0, 10),
      });
      onCreated?.(pickedRequest.lab_order_id);
      onOpenChange(false);
    } catch (err) {
      console.error('[CreateLabOrder] billing an existing request failed:', err);
      setError(err instanceof Error ? err.message : 'Could not add these tests to the invoice.');
    } finally {
      setBusy(false);
    }
  }, [db, pickedRequest, busy, billableTestIds, orgId, onCreated, onOpenChange]);

  const handleCreate = useCallback(async () => {
    if (!db || !effectiveContext || busy || testCount === 0) return;
    setBusy(true);
    setError(null);
    try {
      const { orderId, testIds } = await createLabOrder({
        db,
        orgId,
        clinicId: effectiveContext.clinicId ?? null,
        patientId: effectiveContext.patientId,
        visitId: effectiveContext.visitId ?? null,
        encounterId: effectiveContext.encounterId ?? null,
        appointmentId: effectiveContext.appointmentId ?? null,
        queueId: effectiveContext.queueId ?? null,
        orderingProviderId: effectiveContext.orderingProviderId ?? null,
        actor: { uid: user?.uid ?? '', name: user?.displayName ?? user?.email ?? null },
        source,
        tests: drafts,
        notes: notes.trim() || null,
      });

      // Billing is a separate, optional step on the in-chart surfaces — a
      // failure here must not undo an order that is already live. PRD.329 D5:
      // it is SHOWN (the drawer stays open on a "created, not billed" notice)
      // instead of being swallowed into the console.
      if (canBill && addToInvoice) {
        try {
          await billLabTests({
            db,
            orgId,
            patientId: effectiveContext.patientId,
            patientName: effectiveContext.patientName,
            testIds,
            encounterId: effectiveContext.encounterId ?? null,
            visitId: effectiveContext.visitId ?? null,
            date: new Date().toISOString().slice(0, 10),
          });
        } catch (billErr) {
          console.error('[CreateLabOrder] order created but billing failed:', billErr);
          onCreated?.(orderId);
          setBillingFailure(
            `The order was created, but adding it to the invoice failed${
              billErr instanceof Error ? ` (${billErr.message})` : ''
            }. Bill it later from the order's Pay step in Laboratory → Flow.`,
          );
          return;
        }
      }

      onCreated?.(orderId);
      onOpenChange(false);
    } catch (err) {
      console.error('[CreateLabOrder] failed:', err);
      setError(err instanceof Error ? err.message : 'Could not create the laboratory order.');
    } finally {
      setBusy(false);
    }
  }, [
    db,
    effectiveContext,
    busy,
    testCount,
    drafts,
    orgId,
    user,
    source,
    notes,
    canBill,
    addToInvoice,
    onCreated,
    onOpenChange,
  ]);

  /**
   * PRD.329 — create the requisition: order + tests + dedicated invoice + lines
   * in ONE local write transaction (`createLabRequisition`). On failure nothing
   * was committed; the drawer keeps `requisitionId`, so Retry converges.
   */
  const handleCreateRequisition = useCallback(async () => {
    if (!db || !pickedPatient || busy || testCount === 0) return;
    setBusy(true);
    setError(null);
    try {
      const orderedAt = requisitionInstant(requisitionDate);
      const result = await createLabRequisition({
        db,
        input: {
          requisitionId,
          orgId,
          patientId: pickedPatient.id,
          patientName: pickedPatient.name,
          encounterId: anchor.encounterId,
          visitId: anchor.visitId,
          orderingProviderId: referrer && referrer !== EXTERNAL_REFERRER ? referrer : null,
          referringDoctorName: referrer === EXTERNAL_REFERRER ? externalReferrer : null,
          priority,
          clinicalNotes: notes,
          source: 'laboratory',
          actor: { uid: user?.uid ?? '', name: user?.displayName ?? user?.email ?? null },
          drafts,
          // D6 — no invoice when the Invoice module is off or the user can't bill.
          bill: canBill,
          orderedAt,
          invoiceDate: localDateTimeValue(new Date(orderedAt)).slice(0, 10),
          now: new Date().toISOString(),
        },
      });
      setCreated(result);
      setStep('done');
      onCreated?.(result.orderId);
    } catch (err) {
      console.error('[CreateLabOrder] requisition failed:', err);
      setError(
        `${err instanceof Error ? err.message : 'Could not create the requisition.'} Nothing was saved — you can retry.`,
      );
    } finally {
      setBusy(false);
    }
  }, [
    db,
    pickedPatient,
    busy,
    testCount,
    requisitionDate,
    requisitionId,
    orgId,
    anchor,
    referrer,
    externalReferrer,
    priority,
    notes,
    user,
    drafts,
    canBill,
    onCreated,
  ]);

  const referrerValid = referrer !== EXTERNAL_REFERRER || externalReferrer.trim().length > 0;
  const inChartOrderForm = !wizard && step === 'order';
  const requisitionForm = wizard && step === 'requisition';

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="New Lab Order"
      description={
        wizard
          ? step === 'search'
            ? 'Search a patient or pick a lab request to begin.'
            : step === 'choose'
              ? 'Choose a lab request, or continue with a new requisition.'
              : step === 'order'
                ? `Billing — ${pickedPatient?.name ?? ''}`
                : step === 'requisition'
                  ? `Requisition — ${pickedPatient?.name ?? ''}`
                  : step === 'review'
                    ? `Review & invoice — ${pickedPatient?.name ?? ''}`
                    : step === 'pay'
                      ? `Pay — ${pickedPatient?.name ?? ''}`
                      : `Requisition created — ${pickedPatient?.name ?? ''}`
          : effectiveContext?.patientName
      }
      width={wizard ? DRAWER_WIDTH_BY_PRESET[drawerWidth] : 520}
      portalStyle={brandTokens}
      footer={
        // Steps 1–2 advance by selection, exactly like New Pharmacy Sale — a
        // footer there would imply a commit that hasn't been configured yet.
        wizard && (step === 'search' || step === 'choose') ? undefined : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--sp-3)',
            }}
          >
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
              {step === 'done' || step === 'pay'
                ? created?.orderNumber
                  ? `${created.orderNumber} is in Laboratory → Flow`
                  : ''
                : billingFailure
                  ? 'Order created — not billed'
                  : pickedRequest
                    ? billableTestIds.length === 0
                      ? 'Every test is already invoiced'
                      : `${billableTestIds.length} test${
                          billableTestIds.length === 1 ? '' : 's'
                        } to bill`
                    : testCount === 0
                      ? 'No tests selected'
                      : `${testCount} test${testCount === 1 ? '' : 's'} selected${
                          totalCents > 0 ? ` · ${formatMoney(totalCents / 100)}` : ''
                        }`}
            </span>
            <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
              {step === 'done' ? (
                <>
                  <Button variant="outline" size="md" onClick={() => onOpenChange(false)}>
                    {created?.invoiceId ? 'Finish / Pay Later' : 'Finish'}
                  </Button>
                  {created?.invoiceId && (
                    <Button variant="gradient" size="md" onClick={() => setStep('pay')}>
                      Pay Now
                    </Button>
                  )}
                </>
              ) : step === 'pay' ? (
                <Button variant="outline" size="md" onClick={() => onOpenChange(false)}>
                  Close
                </Button>
              ) : billingFailure ? (
                <Button variant="outline" size="md" onClick={() => onOpenChange(false)}>
                  Close
                </Button>
              ) : step === 'review' ? (
                <>
                  <Button
                    variant="outline"
                    size="md"
                    onClick={() => setStep('requisition')}
                    disabled={busy}
                  >
                    Back
                  </Button>
                  <Button
                    variant="gradient"
                    size="md"
                    onClick={handleCreateRequisition}
                    disabled={busy || testCount === 0}
                  >
                    {busy
                      ? 'Creating…'
                      : error
                        ? 'Retry'
                        : canBill
                          ? 'Create Order & Invoice'
                          : 'Create Order'}
                  </Button>
                </>
              ) : step === 'requisition' ? (
                <>
                  <Button variant="outline" size="md" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant="gradient"
                    size="md"
                    onClick={() => {
                      setError(null);
                      setStep('review');
                    }}
                    disabled={testCount === 0 || !referrerValid}
                  >
                    Continue
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" size="md" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                  {pickedRequest ? (
                    <Button
                      variant="gradient"
                      size="md"
                      onClick={handleBillRequest}
                      disabled={busy || billableTestIds.length === 0}
                    >
                      {busy ? 'Adding…' : 'Add to Invoice'}
                    </Button>
                  ) : (
                    <Button
                      variant="gradient"
                      size="md"
                      onClick={handleCreate}
                      disabled={busy || testCount === 0 || !effectiveContext}
                    >
                      {busy ? 'Creating…' : 'Create Order'}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        )
      }
    >
      {/* PRD.329 — Pay Now reuses the EXISTING Billing body (the same panel
          Queue's Initial Fee and New Pharmacy Sale embed). No laboratory
          payment UI; closing or failing a payment never touches the order. */}
      {step === 'pay' && created?.invoiceId && pickedPatient ? (
        <BillingInvoicesPanel
          active
          patientId={pickedPatient.id}
          initialEncounterId={anchor.encounterId}
          initialDate={localDateTimeValue(new Date(requisitionInstant(requisitionDate))).slice(
            0,
            10,
          )}
          initialInvoiceId={created.invoiceId}
        />
      ) : (
        /* `Drawer` intentionally leaves the body unpadded — each consumer sets
          its own inset. Match the header/footer's horizontal `--sp-6` so the
          content lines up with the title and buttons. */
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-4)',
            padding: 'var(--sp-5) var(--sp-6)',
          }}
        >
          {error && (
            <div
              role="alert"
              style={{
                padding: 'var(--sp-3)',
                borderRadius: 'var(--radius-md)',
                background: '#fee2e2',
                color: '#991b1b',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              {error}
            </div>
          )}

          {billingFailure && (
            <div
              role="alert"
              style={{
                padding: 'var(--sp-3)',
                borderRadius: 'var(--radius-md)',
                background: '#fef3c7',
                color: '#92400e',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              {billingFailure}
            </div>
          )}

          {/* ═══ Step 1 — Search: patient + today's lab requests ═══
            Mirrors New Pharmacy Sale (PRD.290): existing patients only, results
            appear on focus/typing, and nothing is written by selecting. */}
          {wizard && step === 'search' && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={fieldLabelStyle}>Patient</label>
                <div style={{ position: 'relative' }}>
                  <div style={searchIconStyle}>
                    <NavIcon name="search" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search patient name, Patient No., ID No..."
                    value={patientSearch}
                    onChange={(e) => setPatientSearch(e.target.value)}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                    style={{ ...textInputStyle, paddingLeft: 40 }}
                  />
                </div>

                {(searchFocused || patientSearch.trim().length > 0) && (
                  <div style={resultsBoxStyle}>
                    {patientRows.length === 0 ? (
                      <div style={mutedRowStyle}>No patients found</div>
                    ) : (
                      patientRows.slice(0, 25).map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => choosePatient(p)}
                          style={resultRowStyle}
                        >
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={rowTitleStyle}>{p.full_name ?? 'Unknown'}</div>
                            {(p.phone || p.email) && (
                              <div style={rowSubStyle}>
                                {[p.phone, p.email].filter(Boolean).join(' · ')}
                              </div>
                            )}
                          </div>
                          {p.patient_number && (
                            <span style={rowSubStyle}>P-{p.patient_number}</span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}

                <span style={hintStyle}>
                  Existing patients only. Selecting a patient does not create an invoice.
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={fieldLabelStyle}>Today&rsquo;s Lab Requests</label>
                {todaysRequests.length === 0 ? (
                  <div style={dashedEmptyStyle}>No lab requests waiting to be billed today.</div>
                ) : (
                  <div style={listBoxStyle}>
                    {todaysRequests.map((r) => (
                      <LabRequestListRow
                        key={r.lab_order_id}
                        row={r}
                        showPatient
                        onUse={useRequest}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ═══ Step 2 — Choose: this patient's requests, or a new order ═══ */}
          {wizard && step === 'choose' && pickedPatient && (
            <>
              <PatientSummaryBar patient={pickedPatient} onChange={changePatient} />

              {patientRequests.length === 0 ? (
                <div style={dashedEmptyStyle}>
                  No laboratory requests for this patient yet. Continue below to raise a new order.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
                  {(
                    [
                      ['Today', sections.today],
                      ['Earlier', sections.earlier],
                      ['Already billed', sections.settled],
                    ] as const
                  ).map(([label, rows]) =>
                    rows.length === 0 ? null : (
                      <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <label style={fieldLabelStyle}>{label}</label>
                        <div style={listBoxStyle}>
                          {rows.map((r) => (
                            <LabRequestListRow
                              key={r.lab_order_id}
                              row={r}
                              onUse={useRequest}
                              disabled={!isLabRequestEligible(r)}
                            />
                          ))}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              )}

              <div>
                <Button variant="outline" size="md" onClick={startNewOrder}>
                  Continue with a new requisition
                </Button>
              </div>
            </>
          )}

          {/* ═══ Step 3a — Bill an existing request ═══
            No order is created here: the request already exists, so this only
            adds its still-unbilled tests to the invoice. */}
          {step === 'order' && pickedRequest && (
            <>
              {pickedPatient && (
                <PatientSummaryBar patient={pickedPatient} onChange={changePatient} />
              )}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--sp-3)',
                  padding: 'var(--sp-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border)',
                  background: 'var(--bg-surface-2)',
                }}
              >
                <span style={{ minWidth: 0 }}>
                  <span style={rowTitleStyle}>{pickedRequest.order_number ?? 'Lab request'}</span>
                  <span style={rowSubStyle}>
                    {formatLabDate(pickedRequest.ordered_at)}
                    {pickedRequest.provider_name ? ` · ${pickedRequest.provider_name}` : ''}
                  </span>
                </span>
                <Button variant="outline" size="sm" onClick={() => setStep('choose')}>
                  Change
                </Button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={fieldLabelStyle}>Tests on this request</label>
                <div style={listBoxStyle}>
                  {requestTests.map((t) => {
                    const billed = t.invoice_payment_status != null;
                    return (
                      <div
                        key={t.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--sp-2)',
                          padding: 'var(--sp-3)',
                          borderBottom: '1px solid var(--border)',
                        }}
                      >
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={rowTitleStyle}>{t.test_name}</span>
                          <span style={rowSubStyle}>
                            {billed ? 'Already on an invoice' : 'Not yet billed'}
                          </span>
                        </span>
                        <LabStatusBadge status={t.status} />
                        {t.price_cents_snapshot != null && (
                          <span style={rowSubStyle}>
                            {formatMoney(t.price_cents_snapshot / 100)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <span style={hintStyle}>
                  Only tests that aren&rsquo;t on an invoice yet will be added, so re-opening a
                  partly billed request never charges twice.
                </span>
              </div>
            </>
          )}

          {/* ═══ Step 3 — Requisition (PRD.329) ═══ */}
          {requisitionForm && (
            <>
              {pickedPatient && (
                <PatientSummaryBar patient={pickedPatient} onChange={changePatient} />
              )}

              {/* Canonical identifiers — from the patient record, never retyped. */}
              {pickedPatient && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 'var(--sp-4)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                  }}
                >
                  <span>
                    UHID{' '}
                    <strong style={{ color: 'var(--text)' }}>{pickedPatient.number ?? '—'}</strong>
                  </span>
                  <span>
                    Age{' '}
                    <strong style={{ color: 'var(--text)' }}>
                      {patientAge.years != null ? patientAge.years : '—'}
                    </strong>
                  </span>
                  <span>
                    Sex{' '}
                    <strong style={{ color: 'var(--text)' }}>{patientDetail?.sex ?? '—'}</strong>
                  </span>
                </div>
              )}

              {/* Optional consultation. A walk-in order needs none — the anchor
                columns are nullable precisely so no placeholder visit is
                invented. */}
              {wizard && encounterRows.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={fieldLabelStyle}>
                    Attach to consultation{' '}
                    <span style={{ color: 'var(--text-muted)' }}>(optional)</span>
                  </label>
                  <select
                    value={anchor.encounterId ?? ''}
                    onChange={(e) => setEncounterChoice(e.target.value || null)}
                    style={{ ...textInputStyle, cursor: 'pointer' }}
                  >
                    <option value="">No consultation (walk-in)</option>
                    {encounterRows.map((e) => (
                      <option key={e.id} value={e.id}>
                        {formatLabDate(e.started_at)}
                        {e.status === 'open' ? ' · Open' : ''}
                        {e.provider_name ? ` · ${e.provider_name}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: 'var(--sp-3)',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={fieldLabelStyle}>Requisition date</label>
                  <input
                    type="datetime-local"
                    aria-label="Requisition date"
                    value={requisitionDate}
                    onChange={(e) => setRequisitionDate(e.target.value)}
                    style={textInputStyle}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={fieldLabelStyle}>Priority</label>
                  <div role="radiogroup" aria-label="Priority" style={{ display: 'flex', gap: 6 }}>
                    {LAB_PRIORITIES.map((p) => {
                      const on = priority === p;
                      return (
                        <button
                          key={p}
                          type="button"
                          role="radio"
                          aria-checked={on}
                          onClick={() => setPriority(p)}
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            fontSize: 'var(--font-size-sm)',
                            borderRadius: 'var(--radius-md)',
                            border: `1px solid ${
                              on
                                ? p === 'emergency'
                                  ? 'var(--color-danger)'
                                  : 'var(--color-primary)'
                                : 'var(--border)'
                            }`,
                            background: on
                              ? p === 'emergency'
                                ? '#fee2e2'
                                : 'var(--bg-surface-2)'
                              : 'transparent',
                            color: on && p === 'emergency' ? '#991b1b' : 'var(--text)',
                            fontWeight: on ? 'var(--font-weight-semi)' : undefined,
                            cursor: 'pointer',
                          }}
                        >
                          {LAB_PRIORITY_LABELS[p]}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={fieldLabelStyle}>
                  Referring doctor <span style={{ color: 'var(--text-muted)' }}>(optional)</span>
                </label>
                <select
                  aria-label="Referring doctor"
                  value={referrer}
                  onChange={(e) => setReferrer(e.target.value)}
                  style={{ ...textInputStyle, cursor: 'pointer' }}
                >
                  <option value="">Not specified</option>
                  {activeProviders.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.display_name ?? p.full_name ?? 'Provider'}
                    </option>
                  ))}
                  <option value={EXTERNAL_REFERRER}>Other doctor (outside the clinic)…</option>
                </select>
                {referrer === EXTERNAL_REFERRER && (
                  <input
                    aria-label="External referring doctor"
                    value={externalReferrer}
                    onChange={(e) => setExternalReferrer(e.target.value)}
                    placeholder="Referring doctor's name"
                    style={textInputStyle}
                  />
                )}
              </div>
            </>
          )}

          {/* Optional consultation for the in-chart surfaces is already fixed by
            their context; test selection below is shared by both forms. */}
          {(inChartOrderForm || requisitionForm) && !billingFailure && (
            <>
              {requisitionForm && <label style={fieldLabelStyle}>Tests</label>}

              {catalog.length === 0 ? (
                <LabEmptyState
                  title="No laboratory tests in the catalog"
                  description="Laboratory tests are services marked as a lab test. Add them under Laboratory → Services (or Appointments → Manage Services), then order them here. You can still add a one-off test below."
                />
              ) : (
                <LabRequisitionTestPicker
                  catalog={catalog}
                  selected={selected}
                  onToggle={toggle}
                  search={search}
                  onSearchChange={setSearch}
                  testNotes={testNotes}
                  onTestNoteChange={setTestNote}
                  formatMoney={formatMoney}
                  // Wide Laboratory drawer: the drawer scrolls the whole requisition
                  // (no scroll-inside-scroll). Narrow in-chart drawer: cap the sheets.
                  maxHeight={wizard ? undefined : 360}
                />
              )}

              {/* One-off test */}
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--text)',
                    marginBottom: 'var(--sp-2)',
                  }}
                >
                  Add a one-off test
                </label>
                <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                  <input
                    value={freeText}
                    onChange={(e) => setFreeText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addFreeText();
                      }
                    }}
                    placeholder="Test name"
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      fontSize: 'var(--font-size-sm)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--border)',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                  <Button
                    variant="outline"
                    size="md"
                    onClick={addFreeText}
                    disabled={!freeText.trim()}
                  >
                    Add
                  </Button>
                </div>
                {freeTextTests.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 'var(--sp-2)',
                      marginTop: 'var(--sp-2)',
                    }}
                  >
                    {freeTextTests.map((name) => (
                      <button
                        key={name}
                        type="button"
                        onClick={() => setFreeTextTests((prev) => prev.filter((n) => n !== name))}
                        title="Remove"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 'var(--sp-1)',
                          padding: '2px var(--sp-2)',
                          borderRadius: 'var(--radius-xs)',
                          border: '1px solid var(--border)',
                          background: 'var(--bg-surface-2)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--text)',
                          cursor: 'pointer',
                        }}
                      >
                        {name} ×
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--text)',
                    marginBottom: 'var(--sp-2)',
                  }}
                >
                  {requisitionForm ? 'Brief clinical details' : 'Notes'}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder={
                    requisitionForm
                      ? 'Relevant history, provisional diagnosis, medications (optional)'
                      : 'Clinical notes for the laboratory (optional)'
                  }
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: 'var(--font-size-sm)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border)',
                    outline: 'none',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                  }}
                />
              </div>

              {requisitionForm && !canBill && (
                <span style={hintStyle}>
                  Billing isn&rsquo;t available for your role (or the Invoice module is off), so
                  this requisition will create the order only. It can be billed later from
                  Laboratory → Flow.
                </span>
              )}

              {canBill && inChartOrderForm && (
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 'var(--sp-2)',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={addToInvoice}
                    onChange={(e) => setAddToInvoice(e.target.checked)}
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    Add these tests to the patient&rsquo;s invoice
                    <span
                      style={{
                        display: 'block',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {source === 'consultation'
                        ? 'Leave this off if the patient may have the tests done elsewhere — the clinic can bill when it accepts the request.'
                        : 'Uses the existing billing flow. Ordering without billing is allowed — the laboratory will still see the order.'}
                    </span>
                  </span>
                </label>
              )}
            </>
          )}

          {/* ═══ Step 4 — Review & Invoice (PRD.329 §10) ═══
            Prices come from the canonical catalog snapshot the order will
            store; the total uses the shared invoice summary. The invoice written
            on Create is the authoritative bill. */}
          {wizard && step === 'review' && pickedPatient && (
            <>
              <PatientSummaryBar patient={pickedPatient} onChange={changePatient} />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                  gap: 'var(--sp-3)',
                  padding: 'var(--sp-3)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                <span>
                  <span style={rowSubStyle}>Requisition date</span>
                  {new Date(requisitionInstant(requisitionDate)).toLocaleString()}
                </span>
                <span>
                  <span style={rowSubStyle}>Priority</span>
                  <strong style={{ color: priority === 'emergency' ? '#991b1b' : 'var(--text)' }}>
                    {LAB_PRIORITY_LABELS[priority]}
                  </strong>
                </span>
                <span>
                  <span style={rowSubStyle}>Referring doctor</span>
                  {referrer === EXTERNAL_REFERRER
                    ? externalReferrer.trim()
                    : (activeProviders.find((p) => p.id === referrer)?.display_name ??
                      activeProviders.find((p) => p.id === referrer)?.full_name ??
                      '—')}
                </span>
                {anchor.encounterId && (
                  <span>
                    <span style={rowSubStyle}>Consultation</span>
                    Attached
                  </span>
                )}
              </div>
              {notes.trim() && (
                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
                  <span style={rowSubStyle}>Brief clinical details</span>
                  {notes.trim()}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={fieldLabelStyle}>Laboratory Requisition</label>
                <div style={listBoxStyle}>
                  {drafts.map((d, i) => (
                    <div
                      key={`${d.serviceId ?? 'ft'}-${i}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--sp-3)',
                        padding: 'var(--sp-3)',
                        borderBottom: '1px solid var(--border)',
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={rowTitleStyle}>{d.name}</span>
                        <span style={rowSubStyle}>
                          {[
                            d.departmentName ??
                              (d.serviceId ? 'Other / Unassigned' : 'One-off test'),
                            d.notes,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </span>
                      <span
                        style={{
                          fontSize: 'var(--font-size-sm)',
                          color: d.priceCents == null ? '#92400e' : 'var(--text)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {d.priceCents == null ? 'No price set' : formatMoney(d.priceCents / 100)}
                      </span>
                    </div>
                  ))}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: 'var(--sp-3)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-semi)',
                      background: 'var(--bg-surface-2)',
                    }}
                  >
                    <span>Total</span>
                    <span>{formatMoney(reviewSummary.grandTotal)}</span>
                  </div>
                </div>
                {unpriced.length > 0 && (
                  <span style={{ ...hintStyle, color: '#92400e' }}>
                    {unpriced.length} test{unpriced.length === 1 ? ' has' : 's have'} no catalog
                    price and {unpriced.length === 1 ? 'is' : 'are'} billed at {formatMoney(0)} —
                    adjust the line on the invoice, or set a price under Laboratory → Services.
                  </span>
                )}
                <span style={hintStyle}>
                  {canBill
                    ? 'Create writes the order, its tests and a dedicated invoice together. Payment is optional and comes next.'
                    : 'Billing isn’t available for your role, so only the order will be created.'}
                </span>
              </div>
            </>
          )}

          {/* ═══ Step 5 — Created (PRD.329 §12) ═══ */}
          {wizard && step === 'done' && created && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-2)',
                padding: 'var(--sp-4)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface-2)',
              }}
            >
              <span style={rowTitleStyle}>
                {created.created ? 'Requisition created' : 'Requisition already created'}
              </span>
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
                Lab order <strong>{created.orderNumber ?? '—'}</strong>
                {created.invoiceNo ? (
                  <>
                    {' '}
                    · Invoice <strong>{created.invoiceNo}</strong>
                  </>
                ) : null}
              </span>
              <span style={hintStyle}>
                The order is live in Laboratory → Flow at <em>Ordered</em>. Payment is optional and
                never blocks collection — pay now, or later from the order&rsquo;s Pay step.
              </span>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

export default CreateLabOrderDrawer;
